/**
 * Direct fetch-based upload helpers for avatar and lesson material.
 * Bypasses the oRPC contract so multipart/form-data is sent without going
 * through the OpenAPI link codec (which expects a contract procedure).
 *
 * @module upload
 */

import { refreshAccessToken } from '@api/lib/auth-refresh';
import { storage } from '@api/lib/storage';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

async function uploadWithAuth<T = { url: string }>(baseUrl: string, path: string, formData: FormData): Promise<T> {
	let token = await storage.local.get('accessToken');
	let response = await fetch(`${baseUrl}/api${path}`, {
		method: 'POST',
		headers: token ? { Authorization: `Bearer ${token}` } : {},
		body: formData,
	});

	if (response.status === 401) {
		const refreshToken = await storage.local.get('refreshToken');
		if (refreshToken) {
			token = await refreshAccessToken(baseUrl, refreshToken);
			if (token) {
				response = await fetch(`${baseUrl}/api${path}`, {
					method: 'POST',
					headers: { Authorization: `Bearer ${token}` },
					body: formData,
				});
			}
		}
	}

	if (!response.ok) {
		const err = await response.json().catch(() => ({}));
		throw new Error((err as { message?: string })?.message ?? 'Upload failed');
	}

	return response.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Upload an avatar image. POSTs multipart/form-data to /api/upload/avatar.
 * Uses stored access token and retries once on 401 after refresh.
 */
export async function uploadAvatar(baseUrl: string, file: File): Promise<{ url: string }> {
	const formData = new FormData();
	formData.append('file', file);
	return uploadWithAuth(baseUrl, '/upload/avatar', formData);
}

/**
 * Upload a lesson material file. POSTs multipart/form-data to /api/upload/material.
 * Uses stored access token and retries once on 401 after refresh.
 */
export async function uploadMaterial(baseUrl: string, file: File, lessonId: string): Promise<{ url: string }> {
	const formData = new FormData();
	formData.append('file', file);
	formData.append('lessonId', lessonId);
	return uploadWithAuth(baseUrl, '/upload/material', formData);
}

/**
 * Upload a catalog material file (org-scoped). POSTs to /api/upload/catalog-material.
 * Returns { storageKey } for use with organizations.createMaterial (type FILE).
 */
export async function uploadCatalogMaterial(
	baseUrl: string,
	file: File,
	organizationId: string,
): Promise<{ storageKey: string }> {
	const formData = new FormData();
	formData.append('file', file);
	formData.append('organizationId', organizationId);
	return uploadWithAuth<{ storageKey: string }>(baseUrl, '/upload/catalog-material', formData);
}
